import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useEstimateEditorStore, useEstimateComments, useEstimateEditorSettings } from "@tools";
import { ActionButton, Autocomplete } from "@components";
import { Badge, Grid, IconButton, Paper, Portal, Slide } from "@material-ui/core";
import { CommentTile } from "./components/CommentTile";
import { CommentFilled } from "@assets";
import { Close } from "@material-ui/icons";
import useStyle from "./CommentsDrawer.style";
import { oneOf } from "prop-types";
import classnames from "classnames";

export const CommentsDrawer = observer(({
  size="large",
  className,
}) => {
  const {t} = useTranslation();
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  const { isPresetEditor, hasEditorPrivileges, forceAllowVersionSelect } = useEstimateEditorSettings();
  const classes = useStyle();
  
  const [ drawerOpened, openDrawer ] = useState(false);
  
  const {
    sortedVersions,
    visibilityMode,
  } = editorStore;
  
  const {
    currentVersion,
    visibleComments,
    unreadComments,
  } = commentStore;
  
  // const options = useMemo(() => [
  //   {
  //     key: 0,
  //     name: "All Versions"
  //   },
  //   ...editorStore.estimateVersions.map(({ key, name }) => ({ key, name }))
  // ], [editorStore.estimateVersions, editorStore.estimateVersions?.length]);
  
  const options = useMemo(() => [
    {
      key: 0,
      name: "All Versions"
    },
    ...(
      !hasEditorPrivileges && !visibilityMode && !forceAllowVersionSelect
        ? sortedVersions.filter(({ visible }) => visible)
        : sortedVersions
    )], [
    hasEditorPrivileges, sortedVersions, visibilityMode, forceAllowVersionSelect
  ]);
  
  const handleDrawerOpen = () => {
    if(isPresetEditor)
      return;
    
    // editorStore.resetUnreadComments();
    openDrawer(true);
  }
  const handlePanelClose = () => openDrawer(false);
  
  const handleVersionSelect = async (_, n) => {
    commentStore.setCurrentVersion(n.key);
  }
  
  return (
    <>
      <Badge
        badgeContent={unreadComments}
        color="primary"
        overlap="circular"
        aria-label="unread comments"
        className={classes.badge}
        max={9}
      >
        <ActionButton
          square
          icon={
            <CommentFilled />
          }
          color="secondary"
          onClick={handleDrawerOpen}
          name={t(`views.editor.comments.${unreadComments ? "unread" : "read"}`)}
          className={ classnames("preset-commentsSidebarButton", className) }
          tooltip={!isPresetEditor}
          size={size}
        />
      </Badge>
      <Portal>
        <Slide direction="left" in={drawerOpened} mountOnEnter unmountOnExit>
          <Paper elevation={16} className={classes.drawerContainer}>
            <Grid
              item container
              className={classes.title}
              alignItems="center"
              justifyContent="space-between"
            >
              <h2>
                {t("views.editor.comments.name")}
              </h2>
              <IconButton
                color="primary"
                aria-label="close modal"
                onClick={handlePanelClose}
              >
                <Close />
              </IconButton>
            </Grid>
            <Grid
              item container
              direction="column"
              wrap="nowrap"
              className="p-4 bg-primary h-full overflow-auto"
            >
              <Grid
                item container
                wrap="nowrap"
                className="p-2"
              >
                <Autocomplete
                  options={options}
                  value={currentVersion}
                  onChange={handleVersionSelect}
                  idKey="key"
                  labelKey="name"
                  disableClearable
                  fullWidth
                  className="bg-secondary rounded-2 mr-4"
                  fieldClassName="mb-0"
                />
              </Grid>
              <Grid
                item container
                direction="column"
                wrap="nowrap"
              >
                {
                  visibleComments.map((group) => (
                    <CommentTile
                      key={`${group.version}/${group.path}`}
                      group={group}
                      showVersionName={currentVersion === 0}
                    />
                  ))
                }
                {
                  !visibleComments.length &&
                  <h3 className="p-3 transparent-2 text-md">
                    {t(`views.editor.comments.no_comments.${currentVersion === 0 ? "all" : "version"}`, {
                      name: options.find(o => o.key === currentVersion)?.name
                    })}
                  </h3>
                }
              </Grid>
            </Grid>
          </Paper>
        </Slide>
      </Portal>
    </>
  );
});

CommentsDrawer.propTypes = {
  size: oneOf(["small", "medium", "large"]),
}